import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { Link, navigate } from "raviger";
import Moment from "react-moment";
import { Carousel } from "react-responsive-carousel";
import RangeSlider from "react-bootstrap-range-slider";
import {
  Button,
  Card,
  Col,
  Form as BootstrapForm,
  ListGroup,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faMedkit,
  faCut,
  faEdit,
  faEnvelope,
  faLink,
  faMinusSquare,
  faTimes,
  faUserPlus,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import {
  faHandsHolding,
  faUserDoctorMessage,
  faClawMarks,
  faFolderMedical,
  faHomeHeart,
  faPhonePlus,
  faRightLeft,
  faSplit,
  faSkullCrossbones,
} from "@fortawesome/pro-solid-svg-icons";
import { faClosedCaptioningSlash } from "@fortawesome/pro-regular-svg-icons";

import "react-responsive-carousel/lib/styles/carousel.min.css";
import * as Yup from "yup";
import { Formik } from "formik";

// Local components
import Scrollbar from "../components/Scrollbars";
import { AnimalDeleteModal } from "../components/Modals";
import Header from "../components/Header";
import History from "../components/History";
import AnimalCoverImage from "../components/AnimalCoverImage";
import ShelterlyPrintifyButton from "../components/ShelterlyPrintifyButton";
import LoadingLink from "../components/LoadingLink";
import ActionsDropdown from "../components/ActionsDropdown";

// Contexts and hooks
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from "../components/SystemError";
import { useLocationWithRoutes } from "../hooks";

// Utilities
import { printAnimalCareSchedule } from "./Utils";
import { getFileNameFromUrl, isImageFile } from "../utils/files";
import { useQueryParams } from "raviger";

function AnimalDetailsPublic() {
  const [queryParams] = useQueryParams();
  const {
      id = '',
  } = queryParams;
  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Initial animal data
  const [data, setData] = useState({
    id,
    id_for_incident: "",
    name: "",
    species_string: "",
    sex: "",
    pcolor: "",
    scolor: "",
    microchip: "",
    front_image: null,
  });

  const { getFullLocationFromPath } = useLocationWithRoutes();

  useEffect(() => {
    let unmounted = false;
    const source = axios.CancelToken.source();

    const fetchAnimalData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `/animals/api/public/${id}/`,
          { cancelToken: source.token }
        );

        if (!unmounted) {
          const animal = response.data;
          setData(animal);

          let imageUrls = [animal.front_image, animal.side_image].filter(Boolean);

          if (Array.isArray(animal.extra_images)) {
            const extraImages = [];
            const extraDocuments = [];

            animal.extra_images.forEach((file) => {
              if (isImageFile(file)) {
                extraImages.push(file);
              } else {
                extraDocuments.push(file);
              }
            });

            imageUrls = imageUrls.concat(extraImages);
            setDocuments(extraDocuments);
          }

          setImages(imageUrls);
        }
      } catch (error) {
        if (!unmounted) setShowSystemError(true);
      } finally {
        if (!unmounted) setIsLoading(false);
      }
    };

    fetchAnimalData();

    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id, state, setShowSystemError]);

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "300px" }}
      >
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <>
      <div className="row" style={{marginTop: "20px", marginLeft:"20px", marginRight:"20px"}}>
        {/* Left column */}
        <div className="col-6 d-flex" style={{ marginRight: "-15px" }}>
          <Card className="border rounded d-flex" style={{ width: "100%" }}>
            <Card.Body style={{ marginTop: "-10px", marginBottom: "-6px" }}>
              <div className="d-flex justify-content-between">
                <h4 style={{ paddingTop: "5px" }}>Public Animal #{data.id_for_incident}</h4>
              </div>
              <p className="phr pt-0 mt-1" />
              <ListGroup
                variant="flush"
                style={{ marginTop: "-13px", marginBottom: "-13px" }}
              >
                <ListGroup.Item className="text-capitalize">
                  <div className="row">
                    <span className="col-6">
                      <b>Species:</b> {data.species_string}
                    </span>
                    <span className="col-6">
                      <b>Sex:</b> {data.sex || "Unknown"}
                    </span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item className="text-capitalize">
                  <div className="row">
                    <span className="col-6">
                      <b>Organization:</b> {data.organization_name || "N/A"}
                    </span>
                    <span className="col-6">
                      <b>Primary Color:</b> {data.pcolor || "N/A"}
                    </span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item className="text-capitalize">
                  <div className="row">
                    <span className="col-6">
                      <b>Incident:</b> {data.incident_name || "N/A"}
                    </span>
                    <span className="col-6">
                      <b>Secondary Color:</b> {data.scolor || "N/A"}
                    </span>
                  </div>
                </ListGroup.Item>
                { data.incident_reunification_details && ( 
                <ListGroup.Item className="text-capitalize">
                  <div className="row">
                    <span className="col-12">
                      <b>Reunification Details:</b> {data.incident_reunification_details || "N/A"}
                    </span>
                  </div>
                </ListGroup.Item>
                )}
              </ListGroup>
            </Card.Body>
          </Card>
        </div>
        {/* Right column */}
        <Col xs={6} className="pe-0 d-flex flex-column">
          <div
            className="slide-container flex-grow-1 border rounded p-0"
            style={{ width: "auto", height: "345px" }}
          >
            {images.length < 1 ? (
              <Carousel
                className="carousel-wrapper h-100"
                showThumbs={false}
                showStatus={false}
              >
                <AnimalCoverImage
                  animalSpecies={data.species_string_singular}
                  animalImageSrc={data.front_image}
                  customStyles={{
                    width: "100%",
                    padding: "40px",
                    height: "345px",
                    borderRadius: "3px",
                  }}
                />
              </Carousel>
            ) : (
              <Carousel
                className="carousel-wrapper"
                showThumbs={false}
                showStatus={false}
              >
                {images.map((image) => (
                  <div key={image} className="image-container">
                    <img src={image} alt="Animal" />
                  </div>
                ))}
              </Carousel>
            )}
          </div>
        </Col>
      </div>
    </>
  );
}

export default AnimalDetailsPublic;
